import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import CompanySelector from './CompanySelector';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userJson = localStorage.getItem('user');
  const user = userJson ? JSON.parse(userJson) : null;
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Handle company change
  const handleCompanyChange = (companyId) => {
    // Refresh the page to update all data
    window.location.reload();
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) &&
          !event.target.closest('.hamburger-btn')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Function to determine which links to show based on user role
  const getNavigationLinks = () => {
    const links = [];

    // Operador role - access to Dashboard, Reports, Partners, Checkers and Docks
    if (user && user.role === 'Operador') {
      links.push({ path: "/dashboard", label: "Dashboard" });
      links.push({ path: "/reports", label: "Relatórios" });
      links.push({ path: "/partners", label: "Parceiros" });
      links.push({ path: "/checkers", label: "Conferentes" });
      links.push({ path: "/docks", label: "Docas" });
      links.push({ path: "/painel", label: "Painel" }); // Add Painel for Operador
      return links;
    }

    // Supervisor role - access to all operational tabs
    if (user && user.role === 'Supervisor') {
      links.push({ path: "/", label: "Início" });
      links.push({ path: "/dashboard", label: "Dashboard" });
      links.push({ path: "/checkin", label: "Check-in" });
      links.push({ path: "/checkout", label: "Check-out" });
      links.push({ path: "/reports", label: "Relatórios" });
      links.push({ path: "/partners", label: "Parceiros" });
      links.push({ path: "/checkers", label: "Conferentes" });
      links.push({ path: "/docks", label: "Docas" });
      links.push({ path: "/painel", label: "Painel" });
      links.push({ path: "/diaristas", label: "Diaristas" });
      return links;
    }

    // Administrador role - access to everything
    if (user && user.role === 'Administrador') {
      links.push({ path: "/", label: "Início" });
      links.push({ path: "/dashboard", label: "Dashboard" });
      links.push({ path: "/checkin", label: "Check-in" });
      links.push({ path: "/checkout", label: "Check-out" });
      links.push({ path: "/reports", label: "Relatórios" });
      links.push({ path: "/partners", label: "Parceiros" });
      links.push({ path: "/checkers", label: "Conferentes" });
      links.push({ path: "/docks", label: "Docas" });
      links.push({ path: "/painel", label: "Painel" }); // Ensure Painel is included
      links.push({ path: "/diaristas", label: "Diaristas" });
      return links;
    }

    // Default links for other roles
    links.push({ path: "/", label: "Início" });
    links.push({ path: "/dashboard", label: "Dashboard" });
    links.push({ path: "/checkin", label: "Check-in" });
    links.push({ path: "/checkout", label: "Check-out" });
    links.push({ path: "/reports", label: "Relatórios" });
    links.push({ path: "/partners", label: "Parceiros" });
    links.push({ path: "/checkers", label: "Conferentes" });
    links.push({ path: "/docks", label: "Docas" });
    links.push({ path: "/painel", label: "Painel" });

    return links;
  };

  // Function to get user's first name
  const getUserFirstName = (user) => {
    if (user && user.username) {
      // Split by space and take the first part
      return user.username.split(' ')[0];
    }
    return 'Usuário';
  };

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <h1>🚛 NBL Pátio</h1>
        </div>

        {/* Desktop navigation */}
        <nav className="navigation desktop-nav">
          {user && getNavigationLinks().map((link, index) => (
            <Link 
              to={link.path} 
              className={`nav-link ${location.pathname === link.path ? 'active' : ''}`} 
              key={index}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="header-right">
          {user && (
            <div className="user-info">
              <CompanySelector onCompanyChange={handleCompanyChange} />
              <div className="user-menu-container" ref={userMenuRef}>
                <button 
                  className="user-menu-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  {getUserFirstName(user)}
                </button>
                
                {showUserMenu && (
                  <div className="user-dropdown-menu">
                    {user.role === 'Administrador' && (
                      <>
                        <Link 
                          to="/companies" 
                          className="user-dropdown-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Gerenciar Empresas
                        </Link>
                        <Link 
                          to="/users" 
                          className="user-dropdown-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Gerenciar Usuários
                        </Link>
                        <Link 
                          to="/phones" 
                          className="user-dropdown-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Gerenciar Telefones
                        </Link>
                        <Link 
                          to="/messages" 
                          className="user-dropdown-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Gerenciar Mensagens
                        </Link>
                        <div className="dropdown-divider"></div>
                      </>
                    )}
                    <button 
                      onClick={handleLogout} 
                      className="user-dropdown-item logout-item"
                    >
                      Sair
                    </button>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Hamburger button — mobile only */}
          {user && (
            <button
              className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile slide-out menu */}
      {mobileMenuOpen && <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />}
      <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`} ref={mobileMenuRef}>
        <div className="mobile-nav-header">
          <span>Menu</span>
          <button className="mobile-close-btn" onClick={() => setMobileMenuOpen(false)}>✕</button>
        </div>
        {user && getNavigationLinks().map((link, index) => (
          <Link
            to={link.path}
            className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
            key={index}
            onClick={() => setMobileMenuOpen(false)}
          >
            {link.label}
          </Link>
        ))}
        {user && user.role === 'Administrador' && (
          <>
            <div className="mobile-nav-divider" />
            <Link to="/companies" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Gerenciar Empresas</Link>
            <Link to="/users" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Gerenciar Usuários</Link>
            <Link to="/phones" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Gerenciar Telefones</Link>
            <Link to="/messages" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Gerenciar Mensagens</Link>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;